/**
 * Census AIANNH — American Indian / Alaska Native / Native Hawaiian area
 * screen (reservations, off-reservation trust lands, statistical areas).
 *
 * RD-1 (D): refdata-ONLY over the `aiannh` dataset. Overlap with tribal
 * lands is a hard jurisdictional siting factor (fee-to-trust, TERA, EPA
 * treatment-as-state) — surfaced as structured data on the environmental
 * section; the siteResearch LLM narrative remains the qualitative
 * complement.
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { gridCellKey, CACHE_TTL } from './types';
import type { EnvironmentalTribalLands } from '../../types/siteEvidence';
import { refdataContains, refdataIntersect, refdataNear, tryRefdata } from '../refdata/refdataClient';
import type { RefdataFeature } from '../../types/refdata';

const MILE_KM = 1.60934;
const NEAREST_SEARCH_KM = 40; // ~25 mi

function area(feature: RefdataFeature): EnvironmentalTribalLands['areas'][number] {
  const geoid = typeof feature.props.GEOID === 'string' ? feature.props.GEOID
    : typeof feature.props.GEOID === 'number' ? String(feature.props.GEOID) : feature.id;
  return {
    name: typeof feature.props.NAME === 'string' && feature.props.NAME !== ''
      ? feature.props.NAME : 'Unnamed AIANNH area',
    geoid,
    kind: typeof feature.props.LSAD === 'string' && feature.props.LSAD !== '' ? feature.props.LSAD : null,
  };
}

/**
 * Cache contract version. `withCache` reads Firestore BEFORE provider.fetch,
 * and the cache key encodes only LOCATION — so a change in what produces the
 * answer is invisible to it and stale entries shadow refdata indefinitely.
 *
 * Redhead's SED v34 is the proof: `wetlandAcres: 65.7`, source "... (cached)",
 * served from an entry written before the refdata swap, while live refdata at
 * the same coordinates answers from the 0.1-degree snapshot. hifldGrid needed
 * exactly this bump (v2-refdata-snapshot) and got one; the other seven
 * refdata-backed providers did not, which made the whole migration invisible
 * to every existing site until TTL expiry.
 *
 * Bump this string whenever the DATA SOURCE or its shape changes.
 */
const CENSUS_AIANNH_CACHE_CONTRACT_VERSION = 'v2-refdata-snapshot';

export const censusAiannhProvider: DataProvider<EnvironmentalTribalLands> = {
  id: 'census-aiannh',
  name: 'Census AIANNH Tribal Lands',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false,

  cacheKey: ({ lat, lng }) => `${CENSUS_AIANNH_CACHE_CONTRACT_VERSION}_${gridCellKey(lat, lng)}`,

  async fetch(lat, lng, _apiKey?: string, geo?: ProviderGeoContext): Promise<ProviderResult<EnvironmentalTribalLands>> {
    const boundary = geo?.boundary && geo.boundary.length >= 3 ? geo.boundary : undefined;

    const screen = await tryRefdata('aiannh', async () => {
      const overlapping = boundary
        ? (await refdataIntersect('aiannh', boundary, { limit: 10 }))?.hits.map(h => h.feature) ?? null
        : (await refdataContains('aiannh', { lat, lng }))?.features ?? null;
      if (overlapping == null) return null;
      const near = await refdataNear('aiannh', { lat, lng }, NEAREST_SEARCH_KM, 5);
      if (near == null) return null;
      return { overlapping, near };
    });
    if (!screen) {
      throw new Error('Census AIANNH: refdata dataset not available — seed `aiannh` (npm run refdata:ingest:aiannh)');
    }

    const onParcel = screen.overlapping.length > 0;
    const overlapIds = new Set(screen.overlapping.map(f => f.id));
    const nearestOff = screen.near.hits.find(h => !overlapIds.has(h.feature.id)) ?? null;
    const data: EnvironmentalTribalLands = {
      onParcel,
      areas: (onParcel ? screen.overlapping : screen.near.hits.slice(0, 3).map(h => h.feature)).map(area),
      nearestDistanceMi: onParcel
        ? 0
        : nearestOff
          ? Math.round((nearestOff.distanceKm / MILE_KM) * 100) / 100
          : null,
      dataVintage: screen.near.vintageDate,
    };

    return {
      data,
      sources: [{
        field: 'environmental.tribalLands',
        source: `US Census TIGERweb AIANNH (local snapshot, vintage ${screen.near.vintageDate}) — `
          + (onParcel
            ? `site overlaps ${data.areas.map(a => a.name).join(', ')}`
            : data.nearestDistanceMi != null
              ? `nearest tribal land ${data.nearestDistanceMi} mi away`
              : 'no AIANNH areas within 25 mi'),
        url: 'https://tigerweb.geo.census.gov/tigerwebmain/TIGERweb_main.html',
        retrievedAt: new Date().toISOString(),
      }],
    };
  },
};
